import { CalendarClock, CheckCircle2, ClipboardList, Lock, Phone, Wallet } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { formatDate } from '@/utils/format'
import type { PublicAdmissions, SubmitResult } from '../types'

/** Step 6: the reference, the school's message and what happens next. */
export function AdmissionConfirmation({ info, result }: { info: PublicAdmissions; result: SubmitResult }) {
  const { t } = useTranslation('admissions')
  const toBring = info.requiredDocuments.filter((d) => !d.upload)
  return (
    <Card className="p-5 sm:p-7">
      <div className="flex flex-col items-center text-center">
        <span className="mb-3 grid size-12 place-items-center rounded-full bg-success-50 text-success-600 ring-8 ring-success-50/60">
          <CheckCircle2 className="size-6" aria-hidden="true" />
        </span>
        <h2 tabIndex={-1} id="step-heading" className="text-xl font-semibold text-ink focus:outline-none">
          {t('Your application has been sent')}
        </h2>
        <p className="mt-2 text-sm text-muted">{t('Your reference')}</p>
        <p className="mt-1 rounded-control bg-brand-50 px-4 py-2 font-mono text-lg font-semibold tracking-wide break-all text-brand-700">
          {result.reference}
        </p>
        {result.email ? (
          <p className="mt-3 text-sm text-ink-soft">
            {t('A confirmation was sent to {{email}}.', { email: result.email })}
          </p>
        ) : null}
      </div>
      {result.message ? (
        <p className="mt-5 rounded-control border border-line bg-surface-muted/60 p-4 text-sm whitespace-pre-line text-ink-soft">
          {result.message}
        </p>
      ) : null}

      <h3 className="mt-6 mb-3 text-sm font-semibold text-ink">{t('What happens next')}</h3>
      <ol className="space-y-3 text-sm text-ink-soft">
        <li className="flex gap-3">
          <Phone className="mt-0.5 size-4 shrink-0 text-brand-600" aria-hidden="true" />
          <span>{t('The school office reviews the application and contacts you by phone or email.')}</span>
        </li>
        <li className="flex gap-3">
          <ClipboardList className="mt-0.5 size-4 shrink-0 text-brand-600" aria-hidden="true" />
          <span>
            {toBring.length
              ? t('Bring these documents to the school office: {{list}}.', {
                  list: toBring.map((d) => d.label).join(', '),
                })
              : t('Bring the original documents to the school office when you are asked to.')}
          </span>
        </li>
        <li className="flex gap-3">
          <Wallet className="mt-0.5 size-4 shrink-0 text-brand-600" aria-hidden="true" />
          <span>
            {t(
              'Fees are paid at the school office once the application is accepted; nothing is paid online.',
            )}
          </span>
        </li>
      </ol>
      <div className="mt-6 flex flex-col gap-2 sm:flex-row">
        <Button
          asChild
          variant="secondary"
          className="h-auto min-h-10 py-2 text-center leading-snug whitespace-normal"
        >
          <Link to={`/inscription/suivi?ref=${encodeURIComponent(result.reference)}`}>
            {t('Follow my application')}
          </Link>
        </Button>
        <Button
          variant="ghost"
          className="h-auto min-h-10 py-2 whitespace-normal"
          onClick={() => window.print()}
        >
          {t('Print this page')}
        </Button>
      </div>
    </Card>
  )
}

/** Admissions closed: the school's message and the dates. */
export function AdmissionClosed({ info }: { info: PublicAdmissions }) {
  const { t } = useTranslation('admissions')
  const today = new Date().toISOString().slice(0, 10)
  const notYet = info.opensOn !== null && info.opensOn > today
  return (
    <Card className="p-5 text-center sm:p-8">
      <span className="mx-auto mb-3 grid size-12 place-items-center rounded-full bg-surface-sunken text-muted">
        {notYet ? (
          <CalendarClock className="size-6" aria-hidden="true" />
        ) : (
          <Lock className="size-6" aria-hidden="true" />
        )}
      </span>
      <h2 className="text-lg font-semibold text-ink">
        {notYet ? t('Online admissions are not open yet') : t('Online admissions are closed')}
      </h2>
      {info.closedMessage ? (
        <p className="mx-auto mt-3 max-w-lg text-sm whitespace-pre-line text-ink-soft">
          {info.closedMessage}
        </p>
      ) : null}
      {info.opensOn || info.closesOn ? (
        <p className="mt-3 text-sm text-muted">
          {notYet && info.opensOn
            ? t('Applications open on {{date}}.', { date: formatDate(info.opensOn) })
            : info.closesOn && info.closesOn < today
              ? t('Applications closed on {{date}}.', { date: formatDate(info.closesOn) })
              : null}
        </p>
      ) : null}
      <div className="mt-5">
        <Button asChild variant="secondary" className="h-auto min-h-10 py-2 whitespace-normal">
          <Link to="/inscription/suivi">{t('Follow an application already sent')}</Link>
        </Button>
      </div>
    </Card>
  )
}
